// @mui
import { useSettingsContext } from 'src/components/settings';
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';

// ----------------------------------------------------------------------

export default function TestomonialSection() {
  const settings = useSettingsContext();
  const homePageData = useSelector((state) => state.website.homePage);
  const { fetchOnMount } = useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Section Headers
    mainTitle: "",
    mainTitleColor: "#333333",
    subtitle: "",
    subtitleColor: "#8BC34A",
    backgroundColor: "#f8f9fa",

    // Testimonials Data
    testimonials: [
      {
        id: 1,
        quote: "",
        description: "",
        name: "",
        position: "",
        avatar: "",
        rating: 5,
        quoteColor: "#333333",
        descriptionColor: "#666666",
        nameColor: "#333333",
        positionColor: "#888888",
        cardBackgroundColor: "#ffffff",
        cardBorderColor: "#8BC34A"
      },
      {
        id: 2,
        quote: "",
        description: "",
        name: "",
        position: "",
        avatar: "",
        rating: 5,
        quoteColor: "#333333",
        descriptionColor: "#666666",
        nameColor: "#333333",
        positionColor: "#888888",
        cardBackgroundColor: "#ffffff",
        cardBorderColor: "#8BC34A"
      },
      {
        id: 3,
        quote: "",
        description: "",
        name: "",
        position: "",
        avatar: "",
        rating: 5,
        quoteColor: "#333333",
        descriptionColor: "#666666",
        nameColor: "#333333",
        positionColor: "#888888",
        cardBackgroundColor: "#ffffff",
        cardBorderColor: "#8BC34A"
      }
    ],

    // Navigation Colors
    navButtonColor: "#8BC34A",
    navButtonHoverColor: "#4CAF50",

    // Star Rating Colors
    starActiveColor: "#8BC34A",
    starInactiveColor: "#e0e0e0"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updateTestimonial = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((testimonial, i) =>
        i === index ? { ...testimonial, [field]: value } : testimonial
      )
    }));
  };

  const addTestimonial = () => {
    const newId = Math.max(...formData.testimonials.map(t => t.id)) + 1;
    setFormData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, {
        id: newId,
        quote: "New testimonial quote here",
        description: "Add your testimonial description here...",
        name: "Customer Name",
        position: "Job Title",
        avatar: "/assets/images/testimonials/default.jpg",
        rating: 5,
        quoteColor: "#333333",
        descriptionColor: "#666666",
        nameColor: "#333333",
        positionColor: "#888888",
        cardBackgroundColor: "#ffffff",
        cardBorderColor: "#8BC34A"
      }]
    }));
  };

  const removeTestimonial = (index) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    fetchOnMount();
  }, []);
  useEffect(() => {
    if (homePageData?.testimonial_section) {
      // Create a deep copy of the community section to avoid read-only property issues
      const testimonialSection = JSON.parse(JSON.stringify(homePageData.testimonial_section));
      setFormData(prev => ({
        ...prev,
        ...testimonialSection
      }));
    }
  }, [homePageData])


  // const handleSave = (isDraft = false) => {
  //   console.log(isDraft ? 'Saving as draft...' : 'Publishing...', formData);
  //   // You can implement the API call here
  // };

  const handleSave = async () => {

    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.home_page.hero_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(homePageData)),
            testimonial_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }

  };

  const ColorPreview = ({ color, size = 24 }) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid #ddd',
        display: 'inline-block',
        mr: 1
      }}
    />
  );

  const StarRating = ({ rating, activeColor, inactiveColor }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          sx={{
            color: star <= rating ? activeColor : inactiveColor,
            fontSize: '16px'
          }}
        >
          ⭐
        </Box>
      ))}
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Testimonial Section
        </Typography>


        {/* Section Headers */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Section Headers</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Title</Typography>
            <TextField
              fullWidth
              value={formData.mainTitle}
              onChange={(e) => updateFormData('mainTitle', e.target.value)}
              placeholder="Enter main title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.mainTitleColor} />
              <TextField
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Subtitle</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.subtitle}
              onChange={(e) => updateFormData('subtitle', e.target.value)}
              placeholder="Enter subtitle"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Subtitle Color:</Typography>
              <ColorPreview color={formData.subtitleColor} />
              <TextField
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>
        </Box>



        {/* Testimonials Management */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Testimonials Management</Typography>
          </Stack>

          {formData.testimonials.map((testimonial, index) => (
            <Paper key={testimonial.id} variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Testimonial #{testimonial.id}
                </Typography>
                <Button
                  color="error"
                  variant="contained"
                  size="small"
                  startIcon={<Iconify icon="mingcute:close-line" />}
                  onClick={() => removeTestimonial(index)}
                >
                  Remove
                </Button>
              </Stack>

              <Grid container spacing={3}>
                {/* Left Column - Content */}
                <Grid item xs={12} lg={6}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Quote"
                      value={testimonial.quote}
                      onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                      placeholder="Enter testimonial quote"
                    />

                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={4}
                      value={testimonial.description}
                      onChange={(e) => updateTestimonial(index, 'description', e.target.value)}
                      placeholder="Enter detailed testimonial description"
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label="Customer Name"
                          value={testimonial.name}
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                          placeholder="Enter customer name"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Rating</InputLabel>
                          <Select
                            value={testimonial.rating}
                            label="Rating"
                            onChange={(e) => updateTestimonial(index, 'rating', e.target.value)}
                          >
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <MenuItem key={rating} value={rating}>
                                {rating} Star{rating > 1 ? 's' : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      label="Position/Job Title"
                      value={testimonial.position}
                      onChange={(e) => updateTestimonial(index, 'position', e.target.value)}
                      placeholder="Enter job title"
                    />

                    <TextField
                      fullWidth
                      label="Avatar Image URL"
                      value={testimonial.avatar}
                      onChange={(e) => updateTestimonial(index, 'avatar', e.target.value)}
                      placeholder="/assets/images/testimonials/person.jpg"
                      helperText="Recommended size: 80x80px"
                    />
                  </Stack>
                </Grid>

                {/* Right Column - Styling */}
                <Grid item xs={12} lg={6}>
                  <Stack spacing={3}>
                    <Typography variant="subtitle2">Text Colors</Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Quote Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.quoteColor} />
                          <TextField
                            type="color"
                            value={testimonial.quoteColor}
                            onChange={(e) => updateTestimonial(index, 'quoteColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Description Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.descriptionColor} />
                          <TextField
                            type="color"
                            value={testimonial.descriptionColor}
                            onChange={(e) => updateTestimonial(index, 'descriptionColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Name Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.nameColor} />
                          <TextField
                            type="color"
                            value={testimonial.nameColor}
                            onChange={(e) => updateTestimonial(index, 'nameColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Position Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.positionColor} />
                          <TextField
                            type="color"
                            value={testimonial.positionColor}
                            onChange={(e) => updateTestimonial(index, 'positionColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Card Styling</Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Card Background</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.cardBackgroundColor} />
                          <TextField
                            type="color"
                            value={testimonial.cardBackgroundColor}
                            onChange={(e) => updateTestimonial(index, 'cardBackgroundColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Card Border</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={testimonial.cardBorderColor} />
                          <TextField
                            type="color"
                            value={testimonial.cardBorderColor}
                            onChange={(e) => updateTestimonial(index, 'cardBorderColor', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>


                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            color="success"
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={addTestimonial}
            sx={{ mt: 2 }}
          >
            Add New Testimonial
          </Button>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          {/* <Button
            variant="outlined"
            color="inherit"
            onClick={() => handleSave(true)}
          >
            Save as Draft
          </Button> */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={() => handleSave(false)}
          >
            Update
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
